
import { useEffect, useState } from 'react';
import Button from '../../components/base/Button';

export default function SustainabilityPage() {
  const [isVisible, setIsVisible] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    setIsVisible(true);
    
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => new Set(prev).add(entry.target.id));
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const impactPillars = [
    {
      title: 'Employment Creation',
      description: 'Creating sustainable job opportunities for local communities, prioritizing youth and women in our hiring practices.',
      icon: 'ri-team-line',
      stats: '500+ Jobs Created',
      initiatives: [
        'Local hiring preference programs',
        'Youth employment initiatives',
        'Women in mining programs',
        'Skills-based recruitment'
      ],
      image: 'https://public.readdy.ai/ai/img_res/83a59a1beda8b312ff158cb3e45f40d4.jpg'
    },
    {
      title: 'Skills Development',
      description: 'Investing in comprehensive training programs to build technical expertise and leadership capabilities within our workforce.',
      icon: 'ri-graduation-cap-line',
      stats: '1000+ Hours Training',
      initiatives: [
        'Technical skills training',
        'Leadership development',
        'Safety certification programs',
        'Mentorship initiatives'
      ],
      image: 'https://readdy.ai/api/search-image?query=Mining%20training%20facility%2C%20professional%20development%20classroom%2C%20technical%20skills%20training%20for%20mining%20workers%2C%20modern%20educational%20environment%20in%20mining%20industry&width=400&height=300&seq=training&orientation=landscape'
    },
    {
      title: 'Supplier Development',
      description: 'Supporting local businesses and suppliers to create a sustainable economic ecosystem around our operations.',
      icon: 'ri-store-line',
      stats: '200+ Local Suppliers',
      initiatives: [
        'Local procurement programs',
        'Supplier capacity building',
        'Small business support',
        'Value chain development'
      ],
      image: 'https://readdy.ai/api/search-image?query=Local%20African%20business%20suppliers%2C%20small%20business%20development%2C%20community%20economic%20development%2C%20local%20procurement%20in%20mining%20industry&width=400&height=300&seq=suppliers&orientation=landscape'
    },
    {
      title: 'Infrastructure Support',
      description: 'Contributing to essential community infrastructure including water systems, housing, and educational facilities.',
      icon: 'ri-building-line',
      stats: '50+ Projects Completed',
      initiatives: [
        'Water infrastructure development',
        'Housing improvement projects',
        'Educational facility upgrades',
        'Healthcare facility support'
      ],
      image: 'https://readdy.ai/api/search-image?query=Community%20infrastructure%20development%20in%20South%20Africa%2C%20modern%20water%20facilities%2C%20educational%20buildings%2C%20community%20development%20projects&width=400&height=300&seq=infrastructure&orientation=landscape'
    }
  ];

  const environmentalCommitments = [
    {
      title: 'Land Rehabilitation',
      description: 'Comprehensive restoration of mining areas to their natural state',
      icon: 'ri-plant-line'
    },
    {
      title: 'Water Management',
      description: 'Advanced water treatment and conservation systems',
      icon: 'ri-drop-line'
    },
    {
      title: 'Air Quality Control',
      description: 'Dust suppression and emission monitoring programs',
      icon: 'ri-windy-line'
    },
    {
      title: 'Biodiversity Protection',
      description: 'Wildlife conservation and habitat preservation initiatives',
      icon: 'ri-leaf-line'
    },
    {
      title: 'Waste Minimization',
      description: 'Circular economy principles and waste reduction strategies',
      icon: 'ri-recycle-line'
    },
    {
      title: 'Carbon Footprint',
      description: 'Renewable energy adoption and carbon offset programs',
      icon: 'ri-earth-line'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: `linear-gradient(rgba(11, 29, 46, 0.7), rgba(11, 29, 46, 0.7)), url('https://readdy.ai/api/search-image?query=Sustainable%20mining%20landscape%20in%20South%20Africa%2C%20rehabilitated%20mining%20area%20with%20green%20vegetation%2C%20environmental%20restoration%2C%20community%20development%2C%20modern%20sustainable%20mining%20practices&width=1920&height=1080&seq=sustainability-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h1 
            className={`text-5xl md:text-7xl font-bold text-[#2DD4BF] mb-6 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ fontFamily: '"Montserrat", sans-serif' }}
          >
            Building a Positive and Lasting Legacy
          </h1>
          <p 
            className={`text-xl md:text-2xl text-[#E9E3D0] mb-8 transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ fontFamily: '"Open Sans", sans-serif' }}
          >
            Sustainable mining practices that benefit communities and protect our environment
          </p>
        </div>
      </section>

      {/* Community Impact Overview */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="impact-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ease-out ${
              visibleSections.has('impact-header') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Community Impact Pillars
            </h2>
            <p 
              className="text-xl text-[#0B1D2E] max-w-3xl mx-auto"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              Four key areas where we create meaningful, lasting change in the communities we serve
            </p>
          </div>

          <div className="space-y-20">
            {impactPillars.map((pillar, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div 
                  id={`pillar-text-${index}`}
                  data-animate
                  className={`${index % 2 === 1 ? 'lg:col-start-2' : ''} transition-all duration-1000 ease-out ${
                    visibleSections.has(`pillar-text-${index}`) 
                      ? 'opacity-100 translate-x-0' 
                      : `opacity-0 ${index % 2 === 1 ? 'translate-x-10' : '-translate-x-10'}`
                  }`}
                >
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-[#2DD4BF] rounded-full flex items-center justify-center mr-4">
                      <i className={`${pillar.icon} text-2xl text-[#0B1D2E]`}></i>
                    </div>
                    <div>
                      <h3 
                        className="text-3xl font-bold text-[#0B1D2E]"
                        style={{ fontFamily: '"Montserrat", sans-serif' }}
                      >
                        {pillar.title}
                      </h3>
                      <p 
                        className="text-[#2DD4BF] font-bold text-lg"
                        style={{ fontFamily: '"Open Sans", sans-serif' }}
                      >
                        {pillar.stats}
                      </p>
                    </div>
                  </div>
                  
                  <p 
                    className="text-lg text-[#0B1D2E] mb-8 leading-relaxed"
                    style={{ fontFamily: '"Open Sans", sans-serif' }}
                  >
                    {pillar.description}
                  </p>

                  <div className="space-y-3 mb-8">
                    <h4 
                      className="text-xl font-semibold text-[#14B8A6] mb-4"
                      style={{ fontFamily: '"Montserrat", sans-serif' }}
                    >
                      Key Initiatives
                    </h4>
                    {pillar.initiatives.map((initiative, initIndex) => (
                      <div key={initIndex} className="flex items-center">
                        <i className="ri-check-line text-[#2DD4BF] mr-3"></i>
                        <span 
                          className="text-[#0B1D2E]"
                          style={{ fontFamily: '"Open Sans", sans-serif' }}
                        >
                          {initiative}
                        </span>
                      </div>
                    ))}
                  </div>

                  <Button 
                    variant="primary" 
                    size="lg" 
                    className="bg-[#2DD4BF] text-[#0B1D2E] hover:bg-[#14B8A6] hover:transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: '"Montserrat", sans-serif' }}
                  >
                    Learn More
                  </Button>
                </div>

                <div 
                  id={`pillar-image-${index}`}
                  data-animate
                  className={`${index % 2 === 1 ? 'lg:col-start-1' : ''} transition-all duration-1000 ease-out delay-300 ${
                    visibleSections.has(`pillar-image-${index}`) 
                      ? 'opacity-100 translate-x-0' 
                      : `opacity-0 ${index % 2 === 1 ? '-translate-x-10' : 'translate-x-10'}`
                  }`}
                >
                  <div className="relative">
                    <img 
                      src={pillar.image}
                      alt={pillar.title}
                      className="w-full h-96 object-cover rounded-2xl shadow-2xl hover:transform hover:scale-105 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D2E]/30 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Environmental Stewardship */}
      <section className="py-20 bg-[#0B1D2E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="environmental-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ease-out ${
              visibleSections.has('environmental-header') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#2DD4BF] mb-6"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Environmental Stewardship
            </h2>
            <p 
              className="text-xl text-[#E9E3D0] max-w-3xl mx-auto"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              Protecting and preserving our natural environment for future generations
            </p>
          </div>

          <div 
            id="environmental-grid"
            data-animate
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-all duration-1000 ease-out delay-300 ${
              visibleSections.has('environmental-grid') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            {environmentalCommitments.map((commitment, index) => (
              <div key={index} className="bg-[#E9E3D0]/10 backdrop-blur-sm p-8 rounded-xl border border-[#2DD4BF]/20 hover:border-[#2DD4BF]/50 transition-all duration-500 hover:transform hover:scale-105">
                <div className="w-16 h-16 bg-[#2DD4BF] rounded-full flex items-center justify-center mb-6">
                  <i className={`${commitment.icon} text-2xl text-[#0B1D2E]`}></i>
                </div>
                <h3 
                  className="text-xl font-bold text-[#2DD4BF] mb-4"
                  style={{ fontFamily: '"Montserrat", sans-serif' }}
                >
                  {commitment.title}
                </h3>
                <p 
                  className="text-[#E9E3D0] leading-relaxed"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  {commitment.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Statistics */}
      <section className="py-20 bg-[#E9E3D0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div 
            id="stats-header"
            data-animate
            className={`text-center mb-16 transition-all duration-1000 ease-out ${
              visibleSections.has('stats-header') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <h2 
              className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Our Impact by Numbers
            </h2>
            <p 
              className="text-xl text-[#0B1D2E] max-w-3xl mx-auto"
              style={{ fontFamily: '"Open Sans", sans-serif' }}
            >
              Measurable results from our commitment to sustainable development
            </p>
          </div>

          <div 
            id="stats-grid"
            data-animate
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-1000 ease-out delay-300 ${
              visibleSections.has('stats-grid') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="bg-white p-8 rounded-2xl shadow-xl text-center hover:shadow-2xl transition-all duration-500 hover:transform hover:scale-105">
              <div className="text-4xl font-bold text-[#2DD4BF] mb-2">500+</div>
              <p className="text-[#0B1D2E] font-semibold" style={{ fontFamily: '"Open Sans", sans-serif' }}>Jobs Created</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-xl text-center hover:shadow-2xl transition-all duration-500 hover:transform hover:scale-105">
              <div className="text-4xl font-bold text-[#14B8A6] mb-2">1000+</div>
              <p className="text-[#0B1D2E] font-semibold" style={{ fontFamily: '"Open Sans", sans-serif' }}>Training Hours</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-xl text-center hover:shadow-2xl transition-all duration-500 hover:transform hover:scale-105">
              <div className="text-4xl font-bold text-[#2DD4BF] mb-2">200+</div>
              <p className="text-[#0B1D2E] font-semibold" style={{ fontFamily: '"Open Sans", sans-serif' }}>Local Suppliers</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-xl text-center hover:shadow-2xl transition-all duration-500 hover:transform hover:scale-105">
              <div className="text-4xl font-bold text-[#14B8A6] mb-2">50+</div>
              <p className="text-[#0B1D2E] font-semibold" style={{ fontFamily: '"Open Sans", sans-serif' }}>Projects Completed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability Goals */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div 
              id="goals-text"
              data-animate
              className={`transition-all duration-1000 ease-out ${
                visibleSections.has('goals-text') 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 -translate-x-10'
              }`}
            >
              <h2 
                className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6"
                style={{ fontFamily: '"Montserrat", sans-serif' }}
              >
                2030 Sustainability Goals
              </h2>
              <p 
                className="text-lg text-[#0B1D2E] mb-8 leading-relaxed"
                style={{ fontFamily: '"Open Sans", sans-serif' }}
              >
                Our ambitious targets for creating lasting positive impact across all our operations and communities.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#2DD4BF] rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-[#0B1D2E]"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#0B1D2E] mb-2">Carbon Neutral Operations</h4>
                    <p className="text-[#0B1D2E]" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                      Achieve net-zero carbon emissions across all mining operations
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#2DD4BF] rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-[#0B1D2E]"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#0B1D2E] mb-2">1000+ Local Jobs</h4>
                    <p className="text-[#0B1D2E]" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                      Double our employment impact in local communities
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#2DD4BF] rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-[#0B1D2E]"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#0B1D2E] mb-2">100% Land Rehabilitation</h4>
                    <p className="text-[#0B1D2E]" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                      Complete restoration of all mining areas to natural state
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div 
              id="goals-image"
              data-animate
              className={`relative transition-all duration-1000 ease-out delay-300 ${
                visibleSections.has('goals-image') 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 translate-x-10'
              }`}
            >
              <img 
                src="https://readdy.ai/api/search-image?query=Sustainable%20mining%20future%20vision%2C%20green%20technology%20in%20mining%2C%20renewable%20energy%20in%20mining%20operations%2C%20environmental%20restoration%2C%20modern%20sustainable%20mining%20equipment&width=600&height=800&seq=sustainability-goals&orientation=portrait"
                alt="Sustainability Goals"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl hover:transform hover:scale-105 transition-all duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#2DD4BF]">
        <div 
          id="sustainability-cta"
          data-animate
          className={`max-w-4xl mx-auto px-6 lg:px-8 text-center transition-all duration-1000 ease-out ${
            visibleSections.has('sustainability-cta') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 
            className="text-4xl md:text-5xl font-bold text-[#0B1D2E] mb-6"
            style={{ fontFamily: '"Montserrat", sans-serif' }}
          >
            Join Our Sustainability Journey
          </h2>
          <p 
            className="text-xl text-[#0B1D2E] mb-8"
            style={{ fontFamily: '"Open Sans", sans-serif' }}
          >
            Partner with us to create positive change for communities and the environment
          </p>
          <div className="flex justify-center">
            <Button 
              variant="secondary" 
              size="lg" 
              href="/contact" 
              className="bg-[#0B1D2E] text-white hover:bg-[#0B1D2E]/90 hover:transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ fontFamily: '"Montserrat", sans-serif' }}
            >
              Get in Touch
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
